import {createContext, Dispatch, ReactNode, SetStateAction, useCallback, useContext, useEffect, useState} from "react";
import {useLocation} from "react-router-dom";
import {usePaginate} from "../PaginateProvider";
import {CovenantsGroupService} from "../../services/Proposals/CovenantsGroupService";

interface ContextProps {
    covenantsGroup: any[],
    setCovenantsGroup: Dispatch<SetStateAction<any[]>>,
    isLoading: boolean,
    isLoadingItem: boolean,
    error: string,
    params: any,
    setParams: Dispatch<SetStateAction<any>>,
    handleList: () => Promise<any>,
    handleGet: (productId?: number, financialId?: number) => Promise<any>,
    handleInative: (id: number | null) => Promise<any>,
    handleDeleteItem: (id: number | null) => Promise<any>,
    handleSave: (payload: any) => Promise<any>,
    handleSaveItem: (payload: any) => Promise<any>,
}

export const CovenantsGroupContext = createContext<ContextProps>({} as ContextProps);

interface CovenantsGroupProviderProps {
    children: ReactNode
}

export const CovenantsGroupProvider = ({children}: CovenantsGroupProviderProps) => {
    const [covenantsGroup, setCovenantsGroup] = useState<any[]>([]);
    const [error, setError] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isLoadingItem, setIsLoadingItem] = useState<boolean>(false);
    const [params, setParams] = useState<any>({page: 1});

    const {setPagesPaginate} = usePaginate();
    const location = useLocation();
    const service = new CovenantsGroupService();

    const handleList = useCallback(async () => {
        if (!location.pathname.includes('/proposal-track/covenants/groups/list')) {
            return false;
        }

        try {
            setIsLoading(true);
            const [_Response, _Error] = await service.list(params);

            if (!!_Error) {
                setIsLoading(false);
                return setError(_Response || _Error);
            }

            setCovenantsGroup(_Response.data);
            setPagesPaginate(_Response.pages);

            return setIsLoading(false);
        } catch (e) {
            console.warn(e);
            return setIsLoading(false);
        }
    }, [location.pathname, params]);

    const handleGet = async (productId?: number, financialId?: number) => {
        setIsLoading(true);

        try {
            const [_Response, _Error] = await service.get({productId, financialId});

            if (!!_Error) {
                setIsLoading(false);
                return setError(_Response || _Error);
            }

            setCovenantsGroup(_Response.data);

            return setIsLoading(false);
        } catch (e) {
            console.warn(e);
            return setIsLoading(false);
        }
    };

    const handleSave = async (payload: any) => {
        setIsLoading(true);

        try {
            const [_Response, _Error] = await service.save(payload);

            if (!!_Error) {
                setIsLoading(false);
                setError(_Response || _Error);
                return _Error;
            }


            setIsLoading(false);
            return "";
        } catch (e) {
            console.warn(e);
            setIsLoading(false);
            return e;
        }
    }

    const handleInative = async (id: number | null) => {
        setIsLoading(true);

        try {
            const [_Response, _Error] = await service.delete(id);

            if (!!_Error) {
                setIsLoading(false);
                setError(_Response || _Error);
                return _Error;
            }


            setIsLoading(false);
            return "";
        } catch (e) {
            console.warn(e);
            setIsLoading(false);
            return e;
        }
    }


    const handleSaveItem = async (payload: any) => {
        setIsLoadingItem(true);

        try {
            const [_Response, _Error] = await service.saveItem(payload);

            if (!!_Error) {
                setIsLoadingItem(false);
                setError(_Response || _Error);
                return _Error;
            }


            setIsLoadingItem(false);
            return "";
        } catch (e) {
            console.warn(e);
            setIsLoadingItem(false);
            return e;
        }
    }

    const handleDeleteItem = async (id: number | null) => {
        setIsLoadingItem(true);

        try {
            const [_Response, _Error] = await service.deleteItem(id);

            if (!!_Error) {
                setIsLoadingItem(false);
                setError(_Response || _Error);
                return _Error;
            }


            setIsLoadingItem(false);
            return "";
        } catch (e) {
            console.warn(e);
            setIsLoadingItem(false);
            return e;
        }
    }


    useEffect(() => {
        handleList().then();
    }, [handleList]);

    return (
        <CovenantsGroupContext.Provider value={{
            covenantsGroup,
            setCovenantsGroup,
            error,
            params,
            setParams,
            handleList,
            handleGet,
            isLoading,
            handleInative,
            handleSave,
            handleDeleteItem,
            handleSaveItem,
            isLoadingItem,
        }}>
            {children}
        </CovenantsGroupContext.Provider>
    )
}

export const useCovenantsGroup = () => useContext(CovenantsGroupContext);
